// AI-CONTEXT: See docs/features/impact-per-euro-methodiek.md

/**
 * Schaduwtarief voor onbetaalde ondersteunende inzet (methodiek § 2.2):
 * het wettelijk minimumuurloon, halfjaarlijks geïndexeerd. Eén vast normtarief —
 * géén keuze per organisatie. Dit is een ándere grootheid dan de
 * Meetstandaard-parameter `volunteerValuePerHour` (teller-kant): niet mengen.
 *
 * Tabel handmatig bijhouden bij elke halfjaarlijkse indexatie (1 jan / 1 jul).
 * Bron: wettelijk minimumuurloon (rijksoverheid); 2026-waarden bevestigd in het
 * methodiekdocument (Nectar, v1.0).
 *
 * @typedef {object} SchaduwtariefRij
 * @property {string} vanaf - Ingangsdatum (ISO, yyyy-mm-dd)
 * @property {number} tarief - Minimumuurloon in euro's vanaf die datum
 */

/** @type {SchaduwtariefRij[]} — oplopend gesorteerd op ingangsdatum */
const SCHADUWTARIEVEN = [
  { vanaf: '2024-01-01', tarief: 13.27 },
  { vanaf: '2024-07-01', tarief: 13.68 },
  { vanaf: '2025-01-01', tarief: 14.06 },
  { vanaf: '2025-07-01', tarief: 14.40 },
  { vanaf: '2026-01-01', tarief: 14.71 },
  { vanaf: '2026-07-01', tarief: 14.99 },
]

/**
 * Het schaduwtarief dat op een gegeven datum geldt: de laatste tabelrij met een
 * ingangsdatum op of vóór die datum. Datums vóór de eerste rij klemmen op het
 * eerste tarief (het wettelijk minimumUURloon bestaat pas sinds 2024); datums
 * ná de laatste rij krijgen het laatst bekende tarief.
 *
 * @param {Date|string|number} datum - Datum (Date, ISO-string of epoch-ms)
 * @returns {number|null} tarief in euro's, of null bij een ongeldige datum
 */
const schaduwtariefVoorDatum = (datum) => {
  if (datum === null || datum === undefined) return null
  const d = datum instanceof Date ? datum : new Date(datum)
  if (Number.isNaN(d.getTime())) return null

  let tarief = SCHADUWTARIEVEN[0].tarief
  for (const rij of SCHADUWTARIEVEN) {
    if (new Date(rij.vanaf).getTime() <= d.getTime()) {
      tarief = rij.tarief
    }
  }
  return tarief
}

/**
 * Jaargemiddeld schaduwtarief: het gemiddelde van het H1- en H2-tarief van dat
 * kalenderjaar. Gebruikt om ondersteunende-vrijwilligersuren die per boekjaar
 * worden opgegeven (zonder verdeling over de halfjaren) te waarderen.
 *
 * @param {number} jaar - Kalenderjaar (= boekjaar, D3)
 * @returns {number|null} gemiddeld tarief, of null bij een ongeldig jaar
 */
const jaargemiddeldTarief = (jaar) => {
  if (jaar === null || jaar === undefined) return null
  const y = Number(jaar)
  if (!Number.isFinite(y)) return null

  const h1 = schaduwtariefVoorDatum(`${y}-01-01`)
  const h2 = schaduwtariefVoorDatum(`${y}-07-01`)
  if (h1 === null || h2 === null) return null

  return (h1 + h2) / 2
}

export { schaduwtariefVoorDatum, jaargemiddeldTarief, SCHADUWTARIEVEN }
